import { Select, Typography } from "antd"
import cx from "clsx"
import { t } from "i18next"
import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"
import { HoverText, Icon, Spinner, Switch, Skeleton } from ".."
import { filterCriteria } from "../../../utils"
import css from "./index.module.less"

function SelectInput(props) {
  const {
    label,
    labelProps,
    labelClass,
    placeholder = t("Please select one of the options"),
    labelIcon,
    skeletonLoading,
    options,
    getOptionLabel,
    getOptionValue,
    suffixIcon,
    prefixIcon,
    openIcon,
    showSearch,
    serverProps,
    horizontal,
    className,
    onChange = () => {},
    errorMsg,
    valueAsObj,
    inputLoading,
    selectGrow,
    toggle,
    zIndex,
    getPopupContainer = true,
    defaultToggleValue = true,
    filterOption = (inputValue, option) => filterCriteria(inputValue, option, getOptionLabel),
    extraContent = () => {},
    ...rest
  } = props
  const [suffix, setSuffix] = useState(suffixIcon)
  const [prefix, setPrefix] = useState(prefixIcon)
  const [isToggle, setIsToggle] = useState(defaultToggleValue ?? false)

  useEffect(() => {
    !!toggle && setIsToggle(defaultToggleValue)
  }, [defaultToggleValue])

  const getOptions = useCallback(() => {
    return options && !!options?.length
      ? options?.map((item, index) => ({
        ...item,
        value: getOptionValue(item),
        label: getOptionLabel(item),
      }))
      : []
  }, [options])

  const handleOnChange = (_val, value) => {
    if (!!toggle) {
      return onChange(_val, value, isToggle)
    }
    onChange(_val, value,)
  }

  const onToggleClick = () => {
    setIsToggle(prev => !prev)
    handleOnChange([])
  }

  const renderSelect = () => {
    return (
      <>
        <Select
          rootClassName={cx(css.select, className)}
          showSearch={showSearch}
          maxTagCount="responsive"
          maxTagPlaceholder={items => <HoverText message={`+${items.length}`} data={items.map(getOptionLabel)} />}
          suffixIcon={
            inputLoading ? (
              <Spinner type="plain" />
            ) : (
              !!suffix && <Icon icon={"AiFillCaretDown"} className="ant-select-suffix" color="#9D9D9D" size={"16px"} />
            )
          }
          placeholder={
            <>
              {prefixIcon && <Icon icon={prefixIcon} />}
              {placeholder && placeholder}
            </>
          }
          onDropdownVisibleChange={() => {
            setSuffix(suffix == suffixIcon ? openIcon : suffixIcon)
            setPrefix(prefix == prefixIcon ? openIcon : suffixIcon)
          }}
          options={getOptions()}
          onChange={handleOnChange}
          filterOption={filterOption}
          status={!!errorMsg ? "error" : undefined}
          dropdownStyle={{ "--select-dropdown-accent-color": rest.accentColor, zIndex: zIndex }}
          {...(getPopupContainer ? { getPopupContainer: triggerNode => triggerNode.parentElement } : {})}
          dropdownMatchSelectWidth={false}
          showArrow
          dropdownRender={menu =>
            toggle ? (
              <>
                <div className="pi-12">
                  <Switch size="small" onChange={onToggleClick} value={isToggle} label={toggle?.label} className="selectSearch" />
                </div>
                {menu}
              </>
            ) : (
              menu
            )
          }
          {...rest}
        />
        {!!errorMsg && (
          <Typography.Text type="danger">
            <small>{errorMsg}</small>
          </Typography.Text>
        )}
      </>
    )
  }

  const renderLabel = () => {
    return (
      label && (
        <label className={labelClass} style={{ display: "block" }}>
          {label}
        </label>
      )
    )
  }

  return (
    <>
      <div>
        {renderLabel()}
        {skeletonLoading ? <Skeleton type="input" /> : renderSelect()}
        {extraContent()}
      </div>
    </>
  )
}

SelectInput.propTypes = {
  label: PropTypes.string,
  labelProps: PropTypes.object,
  placeholder: PropTypes.string,
  labelIcon: PropTypes.string,
  name: PropTypes.string,
  skeletonLoading: PropTypes.bool,
  loading: PropTypes.bool,
  value: PropTypes.any,
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  suffixIcon: PropTypes.string,
  prefixIcon: PropTypes.string,
  openIcon: PropTypes.string,
  showSearch: PropTypes.bool,
  serverProps: PropTypes.object,
  horizontal: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  errorMsg: PropTypes.string,
}

SelectInput.defaultProps = {
  labelIcon: null,
  skeletonLoading: false,
  options: [],
  getOptionLabel: item => item.label || item.name || item.title || "",
  getOptionValue: item => item.value || item.id,
  suffixIcon: "MdKeyboardArrowDown",
  openIcon: "MdKeyboardArrowUp",
  showSearch: true,
  serverProps: null,
  horizontal: false,
  onChange: () => { },
}

export default SelectInput
